import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore, selectHasPermission } from '../../stores/auth'
import { Spinner } from '../../components/ui/Spinner'
import { CarduriSelector } from '../../components/ui/CarduriSelector'
import { ChipuriFiltru } from '../../components/ui/ChipuriFiltru'
import { PanouLateral, SectiunePanou } from '../../components/ui/PanouLateral'
import { CapSortabil, useSortare } from '../../components/ui/Sortare'
import { Explicatie } from '../../components/ui/Explicatie'
import { Pastila } from '../../components/ui/Pastila'
import { ETICHETE_CIFRE } from '@b4l/shared'

/**
 * Fila „Profitabilitate" din Tablou de bord vânzări (02.11.2026): pe fiecare agent și pe fiecare
 * client, vânzările, costul mărfii, serviciile plătite clienților și ce rămâne.
 *
 * 04.11.2026 (Mădălin): managerul care urmărește echipa (agentul conturilor naționale) stă separat,
 * cu portofoliul lui; echipa nu-l mai include. Comparația o alege omul („Compară cu"), ca în
 * Shopify / Stripe / GA4. Textele sunt scrise pentru omul de vânzări, nu pentru contabil.
 *
 * Victor, adminii și managerii de vânzări (`vanzari.profitabilitate:read`). Design în
 * `docs/superpowers/specs/2026-11-02-profitabilitate-agenti-design.md`.
 */

type Perioada = 'luna' | 'trimestru' | 'an' | 'ytd' | 'interval'
type TipComparatie = 'nimic' | 'anterioara' | 'an'
type SursaCost = 'factura' | 'intrari' | 'catalog' | 'lipsa' | 'reducere'
type TipFactura = 'marketing' | 'bonus' | 'raft' | 'altele'
type SursaPerioada = 'manual' | 'text' | 'regula'
type Grup = 'echipa' | 'manager' | 'nationale' | 'faraAgent'

interface Metrici {
  vanzari: number
  cantitate: number
  cost: number
  servicii: number
  profit: number
  marja: number | null
  serviciiPct: number | null
  vanzariFaraCost: number
  deClasificatNr: number
  deClasificatSuma: number
  surseCost: Record<SursaCost, number>
}
interface Costuri {
  salariuTaxe: number
  bonus: number
  /** Partea neta din salariu / bonus; taxele = costul − netul. */
  salariuNet: number
  bonusNet: number
  auto: number
  carburant: number
  /** Partea din carburant fără proces-verbal: luată după șoferul trecut pe mașină (#1048). */
  carburantFaraPv?: number
  total: number
  masini: string[]
  luniLipsa: string[]
}
/** „Costuri flotă nealocate" — vezi `flotaNealocata` din API (#1048). */
interface FlotaNealocata {
  laFirma: number
  faraAgent: number
  faraPv: number
  total: number
  masini: string[]
}
/** `faraCosturi` = niciun salariu completat: profitul după echipă nu e comparabil. */
type StareCosturi = 'completa' | 'estimata' | 'faraCosturi'
type SumeCosturi = Pick<
  Costuri,
  'salariuTaxe' | 'bonus' | 'salariuNet' | 'bonusNet' | 'auto' | 'carburant' | 'total'
>
interface CosturiTotal extends SumeCosturi {
  luniLipsa: string[]
  agentiIncompleti: number
  agenti: number
  stare: StareCosturi
}
interface Agent extends Metrici {
  cheie: string
  nume: string
  national: boolean
  grup: Grup
  clienti: number
  subZero: number
  /** Salariu + taxe, bonus, mașină, carburant pe perioadă. `null` la conturile naționale. */
  costuri: Costuri | null
  dupaCosturi: number | null
  marjaDupaCosturi: number | null
  stareCosturi: StareCosturi | null
  vanzariComparatie: number | null
  profitComparatie: number | null
  dupaCosturiComparatie: number | null
  stareCosturiComparatie: StareCosturi | null
  comparatieCostEstimat: boolean
}
interface LunaCost {
  luna: string
  salariuTaxe: number | null
  bonus: number | null
  salariuNet: number | null
  bonusNet: number | null
  auto: number | null
  carburant: number | null
  carburantFillGo: number
}
interface Client extends Metrici {
  cheie: string
  agentCheie: string
  id: number | null
  nume: string
  national: boolean
  vanzariComparatie: number | null
  profitComparatie: number | null
  comparatieCostEstimat: boolean
  faraVanzari?: boolean
}
interface Nelegate {
  numar: number
  suma: number
  faraCifInCrm: number
  valuta: number
  faraTva: number
}
interface Bloc extends Metrici {
  clienti: number
  subZero: number
  costuri: CosturiTotal | null
  profitDupaCosturi: number
  marjaDupaCosturi: number | null
  comparatie: {
    vanzari: number
    profit: number
    profitDupaCosturi: number
    stareCosturi: StareCosturi | null
    costEstimat: boolean
  } | null
}
interface Comparatie {
  tip: 'anterioara' | 'an'
  interval: { de: string; pana: string }
  eticheta: string
  panaLaAceeasiZi: boolean
}
interface Raport {
  interval: { de: string; pana: string }
  eticheta: string
  inCurs: boolean
  provizoriu: boolean
  luniPartiale: boolean
  compara: TipComparatie
  comparatie: Comparatie | null
  pragMarja: number
  pragServicii: number
  /** Cel care urmărește echipa (agentul conturilor naționale), dacă e unul singur. */
  manager: string | null
  /** Numele managerului de afișat (cum e în alocarea CRM). */
  managerAfisat: string | null
  echipa: Bloc & { luniLipsa: string[] }
  portofoliuManager: {
    direct: Bloc
    nationale: Bloc
    total: Bloc
    clientiPortofoliu: number
    laManagerDinEa: { vanzari: number; clienti: number }
  } | null
  faraAgent: Bloc
  firma: Bloc
  costuriFlotaNealocate?: FlotaNealocata
  nelegate: Nelegate
  agenti: Agent[]
  clienti: Client[]
}
interface Detaliu {
  produse: {
    sku: string
    denumire: string
    cantitate: number
    vanzari: number
    cost: number | null
    vanzariFaraCost: number
    surse: SursaCost[]
  }[]
  facturi: { numar: string; data: string; valoare: number; storno: boolean }[]
  facturiPrimite: {
    id: number
    numar: string | null
    data: string | null
    emitent: string | null
    /** Partea din perioada aleasa. */
    valoare: number
    valoareTotala: number
    luni: string[]
    sursaPerioada: SursaPerioada
    sursaTip: 'manual' | 'automat' | null
    tip: TipFactura | null
  }[]
}

/** Ce a ales omul: perioada (cu pasul înapoi) sau intervalul lui. */
interface Selectie {
  perioada: Perioada
  pas: number
  de?: string
  pana?: string
}
const paramsSelectie = (s: Selectie) =>
  s.perioada === 'interval'
    ? { perioada: s.perioada, de: s.de, pana: s.pana }
    : { perioada: s.perioada, pas: s.pas }

const ETICHETA_SURSA: Record<SursaCost, string> = {
  factura: 'costul de pe factura din ExpertAccounts',
  intrari: 'ultimul preț de intrare (Intrări în gestiune)',
  catalog: 'prețul de intrare din catalogul ExpertAccounts',
  lipsa: 'fără cost',
  reducere: 'notă de reducere, fără marfă — n-are cost',
}
const ETICHETA_TIP: Record<TipFactura, string> = {
  marketing: 'Marketing',
  bonus: 'Bonus de volum',
  raft: 'Raft / listare',
  altele: 'Altele (nu scade profitul)',
}

const lei = (n: number) => n.toLocaleString('ro-RO', { maximumFractionDigits: 0 })
const pct = (n: number | null) =>
  n == null
    ? '—'
    : `${n.toLocaleString('ro-RO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
const dmy = (s: string | null) => (s ? s.split('-').reverse().join('.') : '—')

function Semnat({ n, className = '' }: { n: number; className?: string }) {
  // Pierderea: roșu temperat DOAR pe cifră, cu semn minus — nu tot rândul.
  return (
    <span className={`tabular-nums ${n < 0 ? 'text-b4l-red-dark' : ''} ${className}`}>
      {n < 0 ? '−' : ''}
      {lei(Math.abs(n))}
    </span>
  )
}

/** Variația în procente, sau `null` când nu e cu ce compara (zero sau nimic atunci). */
function procentVariatie(acum: number, trecut: number | null): number | null {
  if (trecut == null || trecut === 0) return null
  return ((acum - trecut) / Math.abs(trecut)) * 100
}

/** „+12% față de T3 2026 (1.234 lei)" — ca în Shopify Analytics, fără formule. */
function textVariatie(acum: number, trecut: number | null, eticheta: string): string {
  const d = procentVariatie(acum, trecut)
  if (trecut == null) return `față de ${eticheta}: nimic atunci`
  if (d == null) return `față de ${eticheta} (0 lei)`
  // Cu o pierdere la mijloc, un procent nu spune nimic omenesc: se spune diferenta in lei.
  if (acum < 0 || trecut < 0) {
    const dif = acum - trecut
    return `${dif >= 0 ? '+' : '−'}${lei(Math.abs(dif))} lei față de ${eticheta} (${trecut < 0 ? '−' : ''}${lei(Math.abs(trecut))} lei)`
  }
  return `${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(0)}% față de ${eticheta} (${trecut < 0 ? '−' : ''}${lei(Math.abs(trecut))} lei)`
}

/** Coloana de comparație din tabele: procentul colorat, cu suma de atunci la hover. */
function Variatie({
  acum,
  trecut,
  eticheta,
}: {
  acum: number
  trecut: number | null
  eticheta: string
}) {
  const d = procentVariatie(acum, trecut)
  if (d == null) return <span className="text-b4l-text-dim">{acum && !trecut ? 'nou' : '—'}</span>
  return (
    <Explicatie text={`${eticheta}: ${trecut! < 0 ? '−' : ''}${lei(Math.abs(trecut!))} lei.`}>
      <span className={d >= 0 ? 'text-b4l-green-dark' : 'text-b4l-red-dark'}>
        {d >= 0 ? '+' : '−'}
        {Math.abs(d).toFixed(0)}%
      </span>
    </Explicatie>
  )
}

/**
 * Calculul pas cu pas, în cuvintele omului de vânzări (Mădălin, 04.11.2026):
 *   Vânzări nete − Costul mărfii − Servicii plătite clienților = Profit din vânzări
 *   − Costurile echipei de vânzări = Profit rămas după echipă.
 * `restrans` arată costurile ca o singură treaptă, care se desface pe salarii, mașini, carburant.
 */
function Cascada({
  m,
  costuri,
  restrans = false,
  etichetaCosturi = ETICHETE_CIFRE.costuriEchipa.eticheta,
  etichetaFinal = ETICHETE_CIFRE.profitDupaEchipa.eticheta,
}: {
  m: Metrici
  costuri?: SumeCosturi | null
  restrans?: boolean
  etichetaCosturi?: string
  etichetaFinal?: string
}) {
  const [desfacut, setDesfacut] = useState(false)
  const [salariiDesfacut, setSalariiDesfacut] = useState(false)
  const max = Math.max(Math.abs(m.vanzari), Math.abs(m.cost) + Math.abs(m.servicii), 1)
  const ramas = costuri ? m.profit - costuri.total : 0
  const rand = (eticheta: ReactNode, v: number, culoare: string, minus = false) => (
    <div className="grid grid-cols-[minmax(0,170px)_1fr_80px] items-center gap-2 text-[13px] sm:grid-cols-[210px_1fr_90px]">
      <span className="text-b4l-text-muted">{eticheta}</span>
      <div className="h-3 rounded bg-b4l-bg-soft">
        <div
          className={`h-3 rounded ${culoare}`}
          style={{ width: `${Math.min(1, Math.abs(v) / max) * 100}%` }}
        />
      </div>
      <span className="text-right tabular-nums">
        {minus && v !== 0 ? '−' : ''}
        <Semnat n={minus ? Math.abs(v) : v} />
      </span>
    </div>
  )
  // Salariile: un singur rand, desfacut la clic pe net si taxe (Alexandru / Victor, 03.11.2026).
  const salarii = costuri ? costuri.salariuTaxe + costuri.bonus : 0
  const sub = (eticheta: string, v: number) =>
    rand(<span className="pl-3 text-[12px]">{eticheta}</span>, v, 'bg-b4l-red/20', true)
  const componente = costuri && (
    <>
      {rand(
        <button
          type="button"
          aria-expanded={salariiDesfacut}
          onClick={() => setSalariiDesfacut((d) => !d)}
          className="inline-flex items-center gap-0.5 text-left text-b4l-blue-dark hover:underline"
        >
          {salariiDesfacut ? '▾' : '▸'} − Salarii și bonusuri
        </button>,
        salarii,
        'bg-b4l-red/30',
        true,
      )}
      {salariiDesfacut && (
        <>
          {sub('Salariu net', costuri.salariuNet)}
          {sub('Taxe salariu', costuri.salariuTaxe - costuri.salariuNet)}
          {sub('Bonus net', costuri.bonusNet)}
          {sub('Taxe bonus', costuri.bonus - costuri.bonusNet)}
        </>
      )}
      {rand('− Mașini', costuri.auto, 'bg-b4l-red/30', true)}
      {rand('− Carburant', costuri.carburant, 'bg-b4l-red/30', true)}
    </>
  )
  return (
    <div className="grid gap-1.5">
      {rand(ETICHETE_CIFRE.vanzariNete.eticheta, m.vanzari, 'bg-b4l-blue/40')}
      {rand(`− ${ETICHETE_CIFRE.costMarfa.eticheta}`, m.cost, 'bg-b4l-red/30', true)}
      {rand(`− ${ETICHETE_CIFRE.serviciiClienti.eticheta}`, m.servicii, 'bg-b4l-red/30', true)}
      {rand(
        <strong className="font-semibold text-b4l-text">
          = {ETICHETE_CIFRE.profitDinVanzari.eticheta}
        </strong>,
        m.profit,
        m.profit < 0 ? 'bg-b4l-red/50' : 'bg-b4l-blue',
      )}
      {costuri && (
        <>
          {restrans ? (
            <>
              {rand(
                <button
                  type="button"
                  aria-expanded={desfacut}
                  onClick={() => setDesfacut((d) => !d)}
                  className="inline-flex items-center gap-0.5 text-left text-b4l-blue-dark hover:underline"
                >
                  {desfacut ? <ChevronDown size={13} /> : <ChevronRight size={13} />}−{' '}
                  {etichetaCosturi}
                </button>,
                costuri.total,
                'bg-b4l-red/30',
                true,
              )}
              {desfacut && <div className="grid gap-1.5 pl-3">{componente}</div>}
            </>
          ) : (
            componente
          )}
          {rand(
            <strong className="font-semibold text-b4l-text">= {etichetaFinal}</strong>,
            ramas,
            ramas < 0 ? 'bg-b4l-red/50' : 'bg-b4l-green',
          )}
        </>
      )}
    </div>
  )
}

const LUNI_SCURT = [
  'ian.',
  'feb.',
  'mar.',
  'apr.',
  'mai',
  'iun.',
  'iul.',
  'aug.',
  'sep.',
  'oct.',
  'nov.',
  'dec.',
]
const etLuna = (l: string) => `${LUNI_SCURT[Number(l.slice(5, 7)) - 1]} ${l.slice(0, 4)}`

const ETICHETA_SURSA_PERIOADA: Record<SursaPerioada, string> = {
  manual: 'corectată',
  text: 'citită din factură',
  regula: 'după data emiterii',
}
const EXPLICATIE_SURSA_PERIOADA: Record<SursaPerioada, string> = {
  manual: 'Perioada a fost corectată de mână.',
  text: 'Perioada e scrisă pe factură (în notă sau în descrierea serviciului).',
  regula:
    'Factura nu spune perioada. Emisă în ultima zi lucrătoare a lunii → luna emiterii; altfel → luna dinainte.',
}
const lunaUrmatoare = (l: string) => {
  const [y, m] = l.split('-').map(Number)
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`
}
/** „sep. 2026", „ian. 2026 – dec. 2026" sau „feb. 2026, iun. 2026". */
function textLuni(luni: string[]): string {
  if (!luni.length) return '—'
  if (luni.length === 1) return etLuna(luni[0])
  const legate = luni.every((l, i) => i === 0 || lunaUrmatoare(luni[i - 1]) === l)
  return legate
    ? `${etLuna(luni[0])} – ${etLuna(luni[luni.length - 1])}`
    : luni.map(etLuna).join(', ')
}

const LUNA_RE = /^\d{4}-\d{2}$/

/** Tipul facturii primite propus de program din textul ei, neconfirmat de nimeni. */
function PastilaAutomat() {
  return (
    <Explicatie text="Propus de program din textul facturii: servicii (e-learning, ads, bannere, prestări servicii) scad profitul; marfă și vouchere → Altele. Alegerea ta îl înlocuiește.">
      <Pastila ton="gri">propus automat</Pastila>
    </Explicatie>
  )
}

/** Perioada la care se refera o factura primita, cu corectarea ei. */
function PerioadaFacturii({
  id,
  luni,
  sursa,
  poateSchimba,
  ocupat,
  onSalveaza,
}: {
  id: number
  luni: string[]
  sursa: SursaPerioada
  poateSchimba: boolean
  ocupat: boolean
  onSalveaza: (perioada: { de: string; pana: string } | null) => void
}) {
  const [editez, setEditez] = useState(false)
  const [de, setDe] = useState(luni[0] ?? '')
  const [pana, setPana] = useState(luni[luni.length - 1] ?? '')
  if (!editez)
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-b4l-text-muted">Pentru:</span>
        <span>{textLuni(luni)}</span>
        <Explicatie text={EXPLICATIE_SURSA_PERIOADA[sursa]}>
          <Pastila ton={sursa === 'regula' ? 'galben' : 'gri'}>
            {ETICHETA_SURSA_PERIOADA[sursa]}
          </Pastila>
        </Explicatie>
        {poateSchimba && (
          <button
            type="button"
            className="text-b4l-blue underline-offset-2 hover:underline"
            onClick={() => {
              setDe(luni[0] ?? '')
              setPana(luni[luni.length - 1] ?? '')
              setEditez(true)
            }}
          >
            Schimbă
          </button>
        )}
      </div>
    )
  const valid = LUNA_RE.test(de) && LUNA_RE.test(pana) && de <= pana
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-1">
        <span className="text-b4l-text-muted">De la</span>
        <input
          id={`perioada-de-${id}`}
          type="month"
          value={de}
          onChange={(e) => setDe(e.target.value)}
          className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1"
        />
      </label>
      <label className="flex items-center gap-1">
        <span className="text-b4l-text-muted">până la</span>
        <input
          id={`perioada-pana-${id}`}
          type="month"
          value={pana}
          onChange={(e) => setPana(e.target.value)}
          className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1"
        />
      </label>
      <button
        type="button"
        disabled={!valid || ocupat}
        className="rounded-md bg-b4l-blue px-2 py-1 text-white disabled:opacity-50"
        onClick={() => {
          onSalveaza({ de, pana })
          setEditez(false)
        }}
      >
        Salvează
      </button>
      {sursa === 'manual' && (
        <button
          type="button"
          disabled={ocupat}
          className="rounded-md border border-b4l-border px-2 py-1"
          onClick={() => {
            onSalveaza(null)
            setEditez(false)
          }}
        >
          Înapoi la automat
        </button>
      )}
      <button
        type="button"
        className="px-2 py-1 text-b4l-text-muted"
        onClick={() => setEditez(false)}
      >
        Renunță
      </button>
      {!valid && (
        <span className="text-b4l-red-dark">Prima lună trebuie să fie înaintea ultimei.</span>
      )}
    </div>
  )
}

/** Avertismentele unui rând: cost lipsă, facturi de clasificat. */
function Semne({ m, prag }: { m: Metrici; prag: number }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {m.vanzariFaraCost !== 0 && (
        <Explicatie
          text={`Pentru ${lei(m.vanzariFaraCost)} lei din vânzări n-am găsit costul de intrare. Profitul nu-l scade, deci poate fi mai mare decât în realitate cu cel mult atât.`}
        >
          <Pastila ton="galben">cost lipsă</Pastila>
        </Explicatie>
      )}
      {m.deClasificatNr > 0 && (
        <Explicatie
          text={`${m.deClasificatNr} ${m.deClasificatNr === 1 ? 'factură primită' : 'facturi primite'} de la client (${lei(m.deClasificatSuma)} lei) încă neclasificate: nu scad profitul până nu spune cineva ce sunt.`}
        >
          <Pastila ton="mov">de clasificat</Pastila>
        </Explicatie>
      )}
      {m.serviciiPct != null && m.serviciiPct > prag && (
        <Explicatie
          text={`Serviciile facturate de client sunt ${pct(m.serviciiPct)} din vânzări. Peste ${prag}% merită o discuție cu contabilitatea despre contract.`}
        >
          <Pastila ton="galben">servicii {pct(m.serviciiPct)}</Pastila>
        </Explicatie>
      )}
    </span>
  )
}

type Card = 'vanzari' | 'profit' | 'marja' | 'sub'
type ChipAgenti = 'toti' | 'slab' | 'cusub' | 'lipsa' | 'clasif'
type ChipClienti = 'toti' | 'sub' | 'slab' | 'serv' | 'lipsa' | 'clasif' | 'fara'
type ColAgent =
  | 'nume'
  | 'vanzari'
  | 'cantitate'
  | 'cost'
  | 'servicii'
  | 'profit'
  | 'dupaCosturi'
  | 'marja'
  | 'clienti'
  | 'var'
type ColClient = 'nume' | 'vanzari' | 'cantitate' | 'cost' | 'servicii' | 'profit' | 'marja' | 'var'

/**
 * Cat asteapta pagina raportul. Un an calculat prima data dureaza ~2 minute (liniile tuturor
 * facturilor din EA); apoi vine din cache. Implicitul de 15 s al `api` il taia mereu.
 */
const TIMP_CALCUL = 180_000

/** Cat de departe in urma se poate merge cu sagetile (perioade); acelasi prag ca in API. */
const PASI_INAPOI = 36

/** Presetarile din lista „Perioada", ca în Shopify / Stripe / GA4. */
const PRESETARI: { cheie: string; eticheta: string; perioada: Perioada; pas: number }[] = [
  { cheie: 'luna:0', eticheta: 'Luna aceasta', perioada: 'luna', pas: 0 },
  { cheie: 'luna:-1', eticheta: 'Luna trecută', perioada: 'luna', pas: -1 },
  { cheie: 'trimestru:0', eticheta: 'Trimestrul acesta', perioada: 'trimestru', pas: 0 },
  { cheie: 'trimestru:-1', eticheta: 'Trimestrul trecut', perioada: 'trimestru', pas: -1 },
  { cheie: 'ytd:0', eticheta: 'Anul până azi', perioada: 'ytd', pas: 0 },
  { cheie: 'an:-1', eticheta: 'Anul trecut', perioada: 'an', pas: -1 },
]
const ETICHETA_COMPARATIE: Record<TipComparatie, string> = {
  nimic: 'Fără comparație',
  anterioara: 'Perioada anterioară',
  an: 'Aceeași perioadă anul trecut',
}

const aziBucuresti = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Bucharest' })

/** Unde ești: echipa, un agent din echipă, clienții directi ai managerului, conturile naționale. */
type Vedere = { tip: 'echipa' } | { tip: 'agent'; cheie: string }

export function FilaProfitabilitate() {
  // Implicit luna trecuta, incheiata: in luna in curs salariile sunt pe toata luna, iar vanzarile
  // doar pe cateva zile, deci primul ecran ar arata o pierdere care nu exista.
  const [sel, setSel] = useState<Selectie>({ perioada: 'luna', pas: -1 })
  const [compara, setCompara] = useState<TipComparatie>('nimic')
  const [card, setCard] = useState<Card>('profit')
  const [vedere, setVedere] = useState<Vedere>({ tip: 'echipa' })
  const [chipA, setChipA] = useState<ChipAgenti>('toti')
  const [chipC, setChipC] = useState<ChipClienti>('toti')
  const [panouAgent, setPanouAgent] = useState<string | null>(null)
  const [panouClient, setPanouClient] = useState<string | null>(null)

  const q = useQuery<Raport>({
    queryKey: ['profitabilitate', sel, compara],
    queryFn: async () =>
      (
        await api.get<Raport>('/profitabilitate', {
          params: { ...paramsSelectie(sel), compara },
          timeout: TIMP_CALCUL,
        })
      ).data,
    placeholderData: (vechi) => vechi,
  })
  const r = q.data

  const agenti = r?.agenti ?? []
  const agent =
    vedere.tip === 'agent' ? (agenti.find((a) => a.cheie === vedere.cheie) ?? null) : null
  const clientiAgent = useMemo(
    () => (r && agent ? r.clienti.filter((c) => c.agentCheie === agent.cheie) : []),
    [r, agent],
  )
  const echipa = agenti.filter((a) => a.grup === 'echipa')

  const deschideAgent = (k: string) => {
    setPanouAgent(null)
    setVedere({ tip: 'agent', cheie: k })
    setChipC('toti')
  }

  return (
    <div className="grid gap-4">
      <AlegerePerioada sel={sel} setSel={setSel} compara={compara} setCompara={setCompara} r={r} />

      {r && <Note r={r} />}

      {q.isLoading && (
        <div className="flex items-center gap-2 text-sm text-b4l-text-muted">
          <Spinner />
          Se calculează… Prima dată pentru o perioadă lungă (un an) poate dura până la 2–3 minute;
          apoi se deschide pe loc.
        </div>
      )}
      {q.isError && (
        <p className="text-sm text-b4l-red-dark">Nu s-au putut citi cifrele. Reîncarcă pagina.</p>
      )}

      {r && (
        <div className={`grid gap-4 ${q.isFetching ? 'opacity-60' : ''}`}>
          <nav
            className="flex flex-wrap items-center gap-1.5 text-sm text-b4l-text-muted"
            aria-label="Unde ești"
          >
            {agent ? (
              <>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md border border-b4l-border bg-b4l-surface px-2 py-0.5 font-medium text-b4l-blue-dark hover:bg-b4l-bg-soft"
                  onClick={() => {
                    setVedere({ tip: 'echipa' })
                    setChipC('toti')
                  }}
                >
                  ‹ Înapoi la echipă
                </button>
                <span>›</span>
                <span className="text-b4l-text">{numeRand(agent, r)}</span>
              </>
            ) : (
              <span>
                Echipa de vânzări
                {r.managerAfisat
                  ? ` — fără ${r.managerAfisat}, care are portofoliul lui mai jos`
                  : ''}
              </span>
            )}
          </nav>

          <Carduri
            r={r}
            bloc={agent ? blocDinAgent(agent) : r.echipa}
            agent={agent}
            card={card}
            setCard={setCard}
          />
          <DetaliuCard
            r={r}
            card={card}
            bloc={agent ? blocDinAgent(agent) : r.echipa}
            agent={agent}
            randuri={agent ? clientiAgent.filter((c) => !c.faraVanzari) : echipa}
          />

          {agent ? (
            <TabelClienti
              r={r}
              clienti={clientiAgent}
              chip={chipC}
              setChip={setChipC}
              onDeschide={setPanouClient}
            />
          ) : (
            <>
              <TabelAgenti r={r} chip={chipA} setChip={setChipA} onDeschide={setPanouAgent} />
              <PortofoliuManager r={r} onClienti={deschideAgent} onPanou={setPanouAgent} />
              <TotalFirma r={r} />
            </>
          )}

          <p className="max-w-[90ch] text-xs text-b4l-text-dim">
            Toate sumele sunt fără TVA. Vânzări nete = facturile noastre către farmacii și
            distribuitori (B2B), după reduceri și retururi. Costul mărfii = prețul cu care am
            cumpărat produsele vândute. Servicii plătite clienților = facturile de marketing, bonus
            de volum sau raft pe care ni le trimit clienții. Profitul rămas după echipă e o
            contribuție, nu profitul net: din el se mai plătesc sediul, depozitul, transportul,
            mostrele și impozitul.
          </p>
        </div>
      )}

      {r && panouAgent && (
        <PanouAgent
          r={r}
          cheie={panouAgent}
          onInchide={() => setPanouAgent(null)}
          onClienti={deschideAgent}
        />
      )}
      {r && panouClient && (
        <PanouClient r={r} cheie={panouClient} sel={sel} onInchide={() => setPanouClient(null)} />
      )}
    </div>
  )
}

/** Numele de pe rând: „Conturi naționale · Victor Ciortan", „Clienții direcți ai lui Victor". */
function numeRand(a: Pick<Agent, 'nume' | 'grup'>, r: Raport): string {
  if (a.grup === 'nationale')
    return r.managerAfisat ? `Conturi naționale · ${r.managerAfisat}` : a.nume
  if (a.grup === 'manager') return `Clienții direcți ai lui ${r.managerAfisat ?? a.nume}`
  return a.nume
}

/** Un agent văzut ca bloc, ca să-l arate aceleași carduri ca echipa. */
function blocDinAgent(a: Agent): Bloc {
  return {
    ...a,
    costuri: a.costuri
      ? {
          ...a.costuri,
          agenti: 1,
          agentiIncompleti: a.costuri.luniLipsa.length ? 1 : 0,
          stare: a.stareCosturi ?? 'completa',
        }
      : null,
    profitDupaCosturi: a.dupaCosturi ?? a.profit,
    marjaDupaCosturi: a.dupaCosturi == null ? a.marja : a.marjaDupaCosturi,
    comparatie:
      a.vanzariComparatie == null && a.profitComparatie == null
        ? null
        : {
            vanzari: a.vanzariComparatie ?? 0,
            profit: a.profitComparatie ?? 0,
            profitDupaCosturi: a.dupaCosturiComparatie ?? a.profitComparatie ?? 0,
            stareCosturi: a.stareCosturiComparatie,
            costEstimat: a.comparatieCostEstimat,
          },
  }
}

const textLipsa = (luni: string[]) => luni.map(etLuna).join(', ')
const scurtZi = (zi: string) =>
  `${Number(zi.slice(8, 10))} ${LUNI_SCURT[Number(zi.slice(5, 7)) - 1]}`

function AlegerePerioada({
  sel,
  setSel,
  compara,
  setCompara,
  r,
}: {
  sel: Selectie
  setSel: (s: Selectie) => void
  compara: TipComparatie
  setCompara: (c: TipComparatie) => void
  r: Raport | undefined
}) {
  const azi = aziBucuresti()
  const [de, setDe] = useState(sel.de ?? `${azi.slice(0, 8)}01`)
  const [pana, setPana] = useState(sel.pana ?? azi)
  const [personalizat, setPersonalizat] = useState(sel.perioada === 'interval')
  const cheie = personalizat ? 'interval' : `${sel.perioada}:${sel.pas}`
  const ePresetare = PRESETARI.some((p) => p.cheie === cheie)
  const intervalValid =
    /^\d{4}-\d{2}-\d{2}$/.test(de) && /^\d{4}-\d{2}-\d{2}$/.test(pana) && de <= pana
  const sageti = !personalizat && sel.perioada !== 'interval'
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="grid gap-0.5 text-xs text-b4l-text-muted">
        Perioada
        <span className="flex items-center gap-1">
          {sageti && (
            <button
              type="button"
              aria-label="Perioada dinainte"
              disabled={sel.pas <= -PASI_INAPOI}
              onClick={() => setSel({ ...sel, pas: Math.max(-PASI_INAPOI, sel.pas - 1) })}
              className="rounded-md border border-b4l-border p-1.5 hover:bg-b4l-bg-soft disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <select
            id="profitabilitate-perioada"
            value={cheie}
            onChange={(e) => {
              const v = e.target.value
              if (v === 'interval') {
                setPersonalizat(true)
                return
              }
              const p = PRESETARI.find((x) => x.cheie === v)
              if (!p) return
              setPersonalizat(false)
              setSel({ perioada: p.perioada, pas: p.pas })
            }}
            className="min-w-[11rem] rounded-md border border-b4l-border bg-b4l-surface px-2 py-1.5 text-sm font-semibold text-b4l-text"
          >
            {PRESETARI.map((p) => (
              <option key={p.cheie} value={p.cheie}>
                {p.eticheta}
              </option>
            ))}
            {!ePresetare && !personalizat && r && <option value={cheie}>{r.eticheta}</option>}
            <option value="interval">Interval ales de mine…</option>
          </select>
          {sageti && (
            <button
              type="button"
              aria-label="Perioada următoare"
              disabled={sel.pas >= 0}
              onClick={() => setSel({ ...sel, pas: Math.min(0, sel.pas + 1) })}
              className="rounded-md border border-b4l-border p-1.5 hover:bg-b4l-bg-soft disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </span>
      </label>
      {personalizat && (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (intervalValid) setSel({ perioada: 'interval', pas: 0, de, pana })
          }}
        >
          <label className="grid gap-0.5 text-xs text-b4l-text-muted">
            De la
            <input
              id="profitabilitate-de"
              type="date"
              value={de}
              max={azi}
              onChange={(e) => setDe(e.target.value)}
              className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1 text-sm text-b4l-text"
            />
          </label>
          <label className="grid gap-0.5 text-xs text-b4l-text-muted">
            Până la
            <input
              id="profitabilitate-pana"
              type="date"
              value={pana}
              max={azi}
              onChange={(e) => setPana(e.target.value)}
              className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1 text-sm text-b4l-text"
            />
          </label>
          <button
            type="submit"
            disabled={!intervalValid}
            className="rounded-md bg-b4l-blue px-3 py-1.5 text-sm font-medium text-b4l-pe-accent disabled:opacity-50"
          >
            Arată
          </button>
        </form>
      )}
      <label className="grid gap-0.5 text-xs text-b4l-text-muted">
        Compară cu
        <select
          id="profitabilitate-compara"
          value={compara}
          onChange={(e) => setCompara(e.target.value as TipComparatie)}
          className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1.5 text-sm text-b4l-text"
        >
          {(Object.keys(ETICHETA_COMPARATIE) as TipComparatie[]).map((k) => (
            <option key={k} value={k}>
              {ETICHETA_COMPARATIE[k]}
            </option>
          ))}
        </select>
      </label>
      {r && (
        <p className="pb-1.5 text-sm text-b4l-text-muted">
          <span className="font-semibold text-b4l-text">{r.eticheta}</span>
          {r.comparatie && (
            <>
              {' '}
              față de <span className="font-semibold text-b4l-text">{r.comparatie.eticheta}</span>
              {r.comparatie.panaLaAceeasiZi && (
                <> · până la aceeași zi ({scurtZi(r.comparatie.interval.pana)})</>
              )}
            </>
          )}
        </p>
      )}
    </div>
  )
}

/** Avertismentele ca note scurte, nu ca paragrafe; detaliul stă în „i". */
function Note({ r }: { r: Raport }) {
  const note: { ton: 'galben' | 'mov' | 'gri'; text: string; detaliu: string }[] = []
  if (r.inCurs)
    note.push({
      ton: 'gri',
      text: 'perioadă în curs',
      detaliu: `Vânzările sunt până azi, salariile pe luna întreagă: profitul rămas după echipă pare mai mic până se încheie perioada.${r.comparatie ? ' Comparația ia aceleași zile, ca să fie corectă.' : ''}`,
    })
  if (r.luniPartiale)
    note.push({
      ton: 'gri',
      text: 'luni incomplete',
      detaliu:
        'Intervalul nu e pe luni întregi: vânzările sunt pe zilele alese, salariile pe lunile întregi atinse.',
    })
  if (r.provizoriu)
    note.push({
      ton: 'galben',
      text: 'provizoriu',
      detaliu:
        'Facturile de marketing și bonusuri de la clienți pentru perioada asta vin de obicei până pe ~25 ale lunii următoare. Până atunci profitul poate scădea.',
    })
  if (r.echipa.luniLipsa.length)
    note.push({
      ton: 'galben',
      text: `salarii necompletate: ${textLipsa(r.echipa.luniLipsa)}`,
      detaliu: `${r.echipa.costuri?.agentiIncompleti ?? 0} din ${r.echipa.costuri?.agenti ?? 0} agenți n-au salariul trecut pe ${textLipsa(r.echipa.luniLipsa)}. Până se completează, profitul rămas după echipă pare mai mare decât e. Se completează în panoul agentului.`,
    })
  if (r.nelegate.numar)
    note.push({
      ton: 'mov',
      text: `${r.nelegate.numar} facturi de la clienți neincluse`,
      detaliu: [
        'Facturi primite de la clienți care NU intră în profit:',
        r.nelegate.faraCifInCrm
          ? `${r.nelegate.faraCifInCrm} de la un client din CRM fără codul fiscal al facturii (completează codul fiscal pe client)`
          : '',
        r.nelegate.valuta ? `${r.nelegate.valuta} în valută (n-avem curs)` : '',
        r.nelegate.faraTva ? `${r.nelegate.faraTva} fără TVA cunoscut (nu ghicim valoarea)` : '',
        r.nelegate.suma ? `În total ${lei(r.nelegate.suma)} lei.` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    })
  if (!note.length) return null
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="De știut">
      {note.map((n) => (
        <Explicatie key={n.text} text={n.detaliu}>
          <Pastila ton={n.ton}>{n.text}</Pastila>
        </Explicatie>
      ))}
    </div>
  )
}

/** Ce spune fiecare card, simplu, și „Ce înseamnă?" cu un exemplu (Mădălin, 04.11.2026). */
const CE_INSEAMNA: Record<Card, { propozitie: string; exemplu: string }> = {
  vanzari: {
    propozitie: 'Cât am facturat clienților, fără TVA.',
    exemplu:
      'Facturile către farmacii și distribuitori, după reduceri și retururi, fără TVA. Exemplu: o factură de 1.210 lei cu TVA intră aici cu 1.000 lei.',
  },
  profit: {
    propozitie: 'Ce rămâne după marfă, serviciile clienților și costurile echipei.',
    exemplu:
      'Exemplu: vinzi de 1.000 lei; marfa a costat 450, clientul ne-a facturat 50 lei marketing → 500 lei profit din vânzări. Agentul costă 300 lei (salariu, mașină, carburant) → rămân 200 lei. Din ei se plătesc încă sediul, depozitul și impozitul, deci nu e profitul net.',
  },
  marja: {
    propozitie: 'Din fiecare 100 lei vânduți, câți rămân după echipă.',
    exemplu:
      'Exemplu: 200 lei rămași din 1.000 lei vânduți = 20%. Pragul de atenție e marja din vânzări sub 15%.',
  },
  sub: {
    propozitie: 'Clienți la care am pierdut bani în perioada asta.',
    exemplu:
      'De obicei pentru că serviciile pe care ni le facturează (marketing, raft, bonus) au trecut de câștigul pe marfă. Exemplu: câștig pe marfă 400 lei, servicii 600 lei → −200 lei.',
  },
}

function CeInseamna({ k }: { k: Card }) {
  return (
    <Explicatie text={CE_INSEAMNA[k].exemplu} focalizabil={false}>
      <span className="text-[11px] text-b4l-blue-dark">Ce înseamnă?</span>
    </Explicatie>
  )
}

/**
 * Comparația profitului rămas după echipă se face doar pe aceeași bază: perioade încheiate, cu
 * salariile completate în ambele. Altfel se compară profitul din vânzări, cu motivul spus scurt.
 */
function comparatieProfit(r: Raport, b: Bloc): { text: string; motiv: string | null } | null {
  if (!r.comparatie || !b.comparatie)
    return r.comparatie
      ? { text: `față de ${r.comparatie.eticheta}: nimic atunci`, motiv: null }
      : null
  const et = r.comparatie.eticheta
  const dinVanzari = (motiv: string) => ({
    text: `profit din vânzări ${textVariatie(b.profit, b.comparatie!.profit, et)}`,
    motiv,
  })
  if (!b.costuri) return { text: textVariatie(b.profit, b.comparatie.profit, et), motiv: null }
  if (r.inCurs || r.luniPartiale)
    return dinVanzari(
      'Salariile sunt pe luni întregi, vânzările doar pe zilele alese: comparăm profitul din vânzări.',
    )
  if (b.comparatie.stareCosturi === 'faraCosturi')
    return dinVanzari(`Pentru ${et} nu sunt trecute salariile: comparăm profitul din vânzări.`)
  if (b.costuri.stare === 'faraCosturi')
    return dinVanzari('Salariile nu sunt trecute încă: comparăm profitul din vânzări.')
  const estimat = b.costuri.stare === 'estimata' || b.comparatie.stareCosturi === 'estimata'
  return {
    text: textVariatie(b.profitDupaCosturi, b.comparatie.profitDupaCosturi, et),
    motiv: estimat ? 'Unele salarii lipsesc într-una din perioade: cifra e estimată.' : null,
  }
}

function Carduri({
  r,
  bloc: b,
  agent,
  card,
  setCard,
}: {
  r: Raport
  bloc: Bloc
  agent: Agent | null
  card: Card
  setCard: (c: Card) => void
}) {
  const cmpP = comparatieProfit(r, b)
  const cuCosturi = !!b.costuri
  const etProfit = cuCosturi
    ? agent
      ? 'Profit rămas după agent'
      : ETICHETE_CIFRE.profitDupaEchipa.eticheta
    : ETICHETE_CIFRE.profitDinVanzari.eticheta
  const linie = (t: ReactNode) => <span className="block">{t}</span>
  return (
    <CarduriSelector<Card>
      eticheta="Ce vrei să vezi"
      ales={card}
      onAlege={setCard}
      carduri={[
        {
          cheie: 'vanzari',
          eticheta: ETICHETE_CIFRE.vanzariNete.eticheta,
          valoare: `${lei(b.vanzari)} lei`,
          detaliu: (
            <>
              {linie(CE_INSEAMNA.vanzari.propozitie)}
              {r.comparatie &&
                linie(
                  textVariatie(b.vanzari, b.comparatie?.vanzari ?? null, r.comparatie.eticheta),
                )}
              <CeInseamna k="vanzari" />
            </>
          ),
          ton: 'albastru',
        },
        {
          cheie: 'profit',
          eticheta: etProfit,
          valoare: (
            <span className="inline-flex items-center gap-1">
              <Semnat n={b.profitDupaCosturi} /> lei
              {b.costuri && b.costuri.luniLipsa.length > 0 && <Steluta />}
            </span>
          ),
          detaliu: (
            <>
              {linie(
                cuCosturi
                  ? `${ETICHETE_CIFRE.profitDinVanzari.eticheta}: ${b.profit < 0 ? '−' : ''}${lei(Math.abs(b.profit))} lei · contribuție, nu profit net`
                  : 'Ce rămâne după marfă și serviciile plătite clienților.',
              )}
              {cmpP && linie(cmpP.text)}
              <CeInseamna k="profit" />
            </>
          ),
          ton: b.profitDupaCosturi < 0 ? 'rosu' : 'verde',
        },
        {
          cheie: 'marja',
          eticheta: cuCosturi ? (agent ? 'Marjă după agent' : 'Marjă după echipă') : 'Marjă',
          valoare: pct(b.marjaDupaCosturi),
          detaliu: (
            <>
              {linie(CE_INSEAMNA.marja.propozitie)}
              {cuCosturi && linie(`din vânzări: ${pct(b.marja)} · atenție sub ${r.pragMarja}%`)}
              <CeInseamna k="marja" />
            </>
          ),
          ton:
            b.marjaDupaCosturi != null && b.marjaDupaCosturi < 0
              ? 'rosu'
              : b.marja != null && b.marja < r.pragMarja
                ? 'galben'
                : 'gri',
        },
        {
          cheie: 'sub',
          eticheta: 'Clienți sub zero',
          valoare: String(b.subZero),
          detaliu: (
            <>
              {linie(`${CE_INSEAMNA.sub.propozitie} Din ${b.clienti} clienți cu vânzări.`)}
              <CeInseamna k="sub" />
            </>
          ),
          ton: b.subZero > 0 ? 'rosu' : 'gri',
        },
      ]}
    />
  )
}

/** Steluța de costuri incomplete. */
function Steluta() {
  return (
    <span className="text-b4l-amber-dark" aria-label="salarii necompletate">
      *
    </span>
  )
}

function DetaliuCard({
  r,
  card,
  bloc: b,
  agent,
  randuri,
}: {
  r: Raport
  card: Card
  bloc: Bloc
  agent: Agent | null
  randuri: (Metrici & { cheie: string; nume: string; vanzariComparatie: number | null })[]
}) {
  const prag = r.pragMarja
  const cadru = (titlu: string, continut: ReactNode, nota?: string) => (
    <section className="grid gap-3 rounded-xl border border-b4l-border bg-b4l-surface p-4">
      <div className="grid gap-0.5">
        <h3 className="text-sm font-semibold">{titlu}</h3>
        <p className="max-w-[80ch] text-xs text-b4l-text-muted">{CE_INSEAMNA[card].exemplu}</p>
      </div>
      {continut}
      {nota && <p className="max-w-[80ch] text-xs text-b4l-text-muted">{nota}</p>}
    </section>
  )
  if (card === 'profit') {
    const cmpP = comparatieProfit(r, b)
    return cadru(
      'Cum se ajunge la profit',
      <>
        <Cascada
          m={b}
          costuri={b.costuri}
          restrans
          etichetaCosturi={agent ? 'Costurile agentului' : ETICHETE_CIFRE.costuriEchipa.eticheta}
          etichetaFinal={
            agent ? 'Profit rămas după agent' : ETICHETE_CIFRE.profitDupaEchipa.eticheta
          }
        />
        {cmpP?.motiv && <p className="text-xs text-b4l-text-muted">{cmpP.motiv}</p>}
      </>,
      b.costuri
        ? `E o contribuție, nu profitul net: din ea se plătesc încă sediul, depozitul, transportul, mostrele și impozitul.${agent ? '' : ' Costurile echipei sunt ale agenților de teren; ale managerului stau în portofoliul lui.'}`
        : 'Fiecare client are aceeași cascadă în panoul lui, cu documentele din spatele fiecărei trepte.',
    )
  }
  const sortate = [...randuri].sort((a, b2) => b2.vanzari - a.vanzari).slice(0, 12)
  if (card === 'vanzari') {
    const max = Math.max(
      1,
      ...sortate.map((x) => Math.max(x.vanzari, r.comparatie ? (x.vanzariComparatie ?? 0) : 0)),
    )
    return cadru(
      r.comparatie ? `Vânzări nete față de ${r.comparatie.eticheta}` : 'Vânzări nete',
      <div className="grid gap-1.5">
        {sortate.map((x) => (
          <div
            key={x.cheie}
            className="grid grid-cols-[minmax(0,140px)_1fr_80px] items-center gap-2 text-[13px] sm:grid-cols-[minmax(0,180px)_1fr_90px]"
          >
            <span className="truncate">{x.nume}</span>
            <div className="grid gap-0.5">
              <div
                className="h-2.5 rounded bg-b4l-blue/60"
                style={{ width: `${(Math.max(0, x.vanzari) / max) * 100}%` }}
              />
              {r.comparatie && (
                <div
                  className="h-1.5 rounded bg-b4l-text-dim/30"
                  style={{ width: `${(Math.max(0, x.vanzariComparatie ?? 0) / max) * 100}%` }}
                />
              )}
            </div>
            <span className="text-right tabular-nums">{lei(x.vanzari)}</span>
          </div>
        ))}
      </div>,
      r.comparatie
        ? `Bara groasă e ${r.eticheta}, cea subțire ${r.comparatie.eticheta}.`
        : 'Alege „Compară cu" ca să vezi și perioada de comparație.',
    )
  }
  if (card === 'marja') {
    const max = Math.max(30, ...sortate.map((x) => Math.abs(x.marja ?? 0)))
    return cadru(
      `Marja din vânzări, față de pragul de ${prag}%`,
      <div className="grid gap-1.5">
        {sortate.map((x) => (
          <div
            key={x.cheie}
            className="grid grid-cols-[minmax(0,140px)_1fr_70px] items-center gap-2 text-[13px] sm:grid-cols-[minmax(0,180px)_1fr_70px]"
          >
            <span className="truncate">{x.nume}</span>
            <div className="h-2.5 rounded bg-b4l-bg-soft">
              <div
                className={`h-2.5 rounded ${(x.marja ?? 0) < 0 ? 'bg-b4l-red/50' : (x.marja ?? 0) < prag ? 'bg-b4l-amber/60' : 'bg-b4l-blue/60'}`}
                style={{ width: `${(Math.abs(x.marja ?? 0) / max) * 100}%` }}
              />
            </div>
            <span
              className={`text-right tabular-nums ${(x.marja ?? 0) < 0 ? 'text-b4l-red-dark' : ''}`}
            >
              {pct(x.marja)}
            </span>
          </div>
        ))}
      </div>,
    )
  }
  const sub = randuri.filter((x) => x.profit < 0).sort((a, b2) => a.profit - b2.profit)
  return cadru(
    'Sub zero',
    sub.length ? (
      <ul className="grid gap-1 text-[13px]">
        {sub.map((x) => (
          <li key={x.cheie} className="flex flex-wrap justify-between gap-2">
            <span>{x.nume}</span>
            <span className="text-b4l-text-muted">
              servicii {lei(x.servicii)} lei ({pct(x.serviciiPct)} din vânzări) · profit{' '}
              <Semnat n={x.profit} />
            </span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-b4l-text-muted">Niciunul în perioada asta.</p>
    ),
  )
}

const coloane = {
  vanzari: `Facturile B2B emise de noi: ${ETICHETE_CIFRE.vanzariNete.detaliu}, după retururi.`,
  cantitate: 'Bucăți vândute (returul scade). Reducerile și serviciile nu sunt bucăți.',
  cost: 'Prețul cu care am cumpărat marfa vândută (din ExpertAccounts), pe fiecare linie.',
  servicii:
    'Facturile de marketing, bonus de volum sau raft pe care ni le trimite clientul. Se leagă de client după codul fiscal.',
  profit: 'Vânzări − costul mărfii − serviciile plătite clienților.',
  dupaCosturi:
    'Profitul din vânzări minus ce costă agentul (estimat): salariu și taxe, bonus, mașină, carburant. Costurile se completează în panoul agentului; carburantul vine singur din Fill&Go.',
  marja: 'Profitul din vânzări, din fiecare 100 lei vânduți.',
}

/** Rândul unui agent (sau al unui bloc) în tabelul echipei. */
function RandAgent({
  a,
  r,
  max,
  bara = true,
  onDeschide,
}: {
  a: Agent
  r: Raport
  max: number
  bara?: boolean
  onDeschide: (k: string) => void
}) {
  return (
    <tr
      tabIndex={0}
      onClick={() => onDeschide(a.cheie)}
      onKeyDown={(e) => e.key === 'Enter' && onDeschide(a.cheie)}
      className="cursor-pointer border-t border-b4l-border hover:bg-b4l-bg-soft"
    >
      <td className="px-2 py-2 font-medium">
        <div className="flex flex-wrap items-center gap-1.5">
          {numeRand(a, r)}
          <Semne m={a} prag={r.pragServicii} />
        </div>
      </td>
      <td className="px-2 py-2 text-right tabular-nums">
        <div className="flex items-center justify-end gap-2">
          {lei(a.vanzari)}
          <BaraVanzari v={bara ? a.vanzari : null} max={max} />
        </div>
      </td>
      <td className="px-2 py-2 text-right tabular-nums">{lei(a.cantitate)}</td>
      <td className="px-2 py-2 text-right tabular-nums">{lei(a.cost)}</td>
      <td className="px-2 py-2 text-right tabular-nums">{a.servicii ? lei(a.servicii) : '—'}</td>
      <td className="px-2 py-2 text-right">
        <Semnat n={a.profit} />
      </td>
      <td className="px-2 py-2 text-right">
        {a.costuri && a.dupaCosturi != null ? (
          <Explicatie
            text={`${a.costuri.total ? `Costurile agentului: ${lei(a.costuri.total)} lei (salarii ${lei(a.costuri.salariuTaxe + a.costuri.bonus)}, mașină ${lei(a.costuri.auto)}, carburant ${lei(a.costuri.carburant)}).` : 'Fără costuri trecute.'}${a.costuri.luniLipsa.length ? ` Salariu necompletat: ${textLipsa(a.costuri.luniLipsa)}.` : ''}`}
          >
            <span className="inline-flex items-center gap-1">
              <Semnat n={a.dupaCosturi} />
              {a.costuri.luniLipsa.length > 0 && <Steluta />}
            </span>
          </Explicatie>
        ) : (
          <span className="text-b4l-text-dim">—</span>
        )}
      </td>
      <td
        className={`px-2 py-2 text-right tabular-nums ${(a.marja ?? 0) < 0 ? 'text-b4l-red-dark' : ''}`}
      >
        {pct(a.marja)}
      </td>
      <td className="px-2 py-2 text-right tabular-nums">
        {a.clienti}
        {a.subZero > 0 && (
          <span className="ml-1.5">
            <Pastila ton="rosu">{a.subZero} sub zero</Pastila>
          </span>
        )}
      </td>
      {r.comparatie && (
        <td className="px-2 py-2 text-right">
          <Variatie
            acum={a.vanzari}
            trecut={a.vanzariComparatie}
            eticheta={r.comparatie.eticheta}
          />
        </td>
      )}
    </tr>
  )
}

function CapAgenti({
  r,
  sortare,
  primul,
}: {
  r: Raport
  sortare: ReturnType<typeof useSortare<ColAgent>>
  primul: string
}) {
  const cap = (k: ColAgent, t: string, explicatie?: string) => (
    <CapSortabil className="!px-2" sortare={sortare} coloana={k} dreapta explicatie={explicatie}>
      {t}
    </CapSortabil>
  )
  return (
    <thead className="text-xs text-b4l-text-muted">
      <tr>
        <CapSortabil className="!px-2" sortare={sortare} coloana="nume">
          {primul}
        </CapSortabil>
        {cap('vanzari', 'Vânzări', coloane.vanzari)}
        {cap('cantitate', 'Buc.', coloane.cantitate)}
        {cap('cost', 'Costul mărfii', coloane.cost)}
        {cap('servicii', 'Servicii clienți', coloane.servicii)}
        {cap('profit', 'Profit din vânzări', coloane.profit)}
        {cap('dupaCosturi', 'Rămas după agent', coloane.dupaCosturi)}
        {cap('marja', 'Marjă', coloane.marja)}
        <CapSortabil className="!px-2" sortare={sortare} coloana="clienti" dreapta>
          Clienți
        </CapSortabil>
        {r.comparatie &&
          cap(
            'var',
            `Față de ${r.comparatie.eticheta}`,
            `Vânzările față de ${r.comparatie.eticheta}.`,
          )}
      </tr>
    </thead>
  )
}

function TabelAgenti({
  r,
  chip,
  setChip,
  onDeschide,
}: {
  r: Raport
  chip: ChipAgenti
  setChip: (c: ChipAgenti) => void
  onDeschide: (k: string) => void
}) {
  const sortare = useSortare<ColAgent>('vanzari', { nume: 'asc' })
  const ag = r.agenti.filter((a) => a.grup === 'echipa')
  const filtre: Record<ChipAgenti, (a: Agent) => boolean> = {
    toti: () => true,
    slab: (a) => a.marja != null && a.marja < r.pragMarja,
    cusub: (a) => a.subZero > 0,
    lipsa: (a) => a.vanzariFaraCost !== 0,
    clasif: (a) => a.deClasificatNr > 0,
  }
  const vizibili = sortare.ordoneaza(ag.filter(filtre[chip]), (a, k) =>
    k === 'var' ? (a.vanzariComparatie ? a.vanzari / a.vanzariComparatie : null) : a[k],
  )
  const max = Math.max(1, ...ag.map((a) => a.vanzari))
  const e = r.echipa
  const coloaneNr = r.comparatie ? 10 : 9
  const medie = (v: number) => (ag.length ? v / ag.length : 0)
  return (
    <section className="grid gap-3" aria-labelledby="titlu-echipa">
      <h3 id="titlu-echipa" className="text-sm font-semibold">
        Agenții echipei
      </h3>
      <ChipuriFiltru<ChipAgenti>
        ales={chip}
        onAlege={setChip}
        chipuri={[
          { cheie: 'toti', eticheta: 'Toți', numar: ag.length },
          {
            cheie: 'slab',
            eticheta: `Marjă sub ${r.pragMarja}%`,
            numar: ag.filter(filtre.slab).length,
          },
          {
            cheie: 'cusub',
            eticheta: 'Cu clienți sub zero',
            numar: ag.filter(filtre.cusub).length,
          },
          { cheie: 'lipsa', eticheta: 'Cost lipsă', numar: ag.filter(filtre.lipsa).length },
          { cheie: 'clasif', eticheta: 'De clasificat', numar: ag.filter(filtre.clasif).length },
        ]}
      />
      <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
        <table className="w-full min-w-[900px] text-sm">
          <CapAgenti r={r} sortare={sortare} primul="Agent" />
          <tbody>
            {vizibili.map((a) => (
              <RandAgent key={a.cheie} a={a} r={r} max={max} onDeschide={onDeschide} />
            ))}
            {!vizibili.length && (
              <tr>
                <td colSpan={coloaneNr} className="px-2 py-6 text-center text-b4l-text-muted">
                  Niciun agent aici.
                </td>
              </tr>
            )}
          </tbody>
          {ag.length > 0 && (
            <tfoot className="text-[13px]">
              <tr className="border-t-2 border-b4l-border font-semibold">
                <td className="px-2 py-2">Total echipă</td>
                <td className="px-2 py-2 text-right tabular-nums">{lei(e.vanzari)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{lei(e.cantitate)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{lei(e.cost)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{lei(e.servicii)}</td>
                <td className="px-2 py-2 text-right">
                  <Semnat n={e.profit} />
                </td>
                <td className="px-2 py-2 text-right">
                  <Semnat n={e.profitDupaCosturi} />
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{pct(e.marja)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{e.clienti}</td>
                {r.comparatie && (
                  <td className="px-2 py-2 text-right">
                    <Variatie
                      acum={e.vanzari}
                      trecut={e.comparatie?.vanzari ?? null}
                      eticheta={r.comparatie.eticheta}
                    />
                  </td>
                )}
              </tr>
              <tr className="text-b4l-text-muted">
                <td className="px-2 py-1.5">Media pe agent</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{lei(medie(e.vanzari))}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{lei(medie(e.cantitate))}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{lei(medie(e.cost))}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{lei(medie(e.servicii))}</td>
                <td className="px-2 py-1.5 text-right">
                  <Semnat n={medie(e.profit)} />
                </td>
                <td className="px-2 py-1.5 text-right">
                  <Semnat n={medie(e.profitDupaCosturi)} />
                </td>
                <td colSpan={coloaneNr - 7} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  )
}

/**
 * Portofoliul managerului (Mădălin, 04.11.2026): toți clienții lui directi, din alocarea CRM (ca în
 * Tablou de bord vânzări), și conturile naționale ca sub-grup. Separat de echipă, pe care o urmărește.
 */
function PortofoliuManager({
  r,
  onClienti,
  onPanou,
}: {
  r: Raport
  onClienti: (k: string) => void
  onPanou: (k: string) => void
}) {
  const sortare = useSortare<ColAgent>('vanzari', { nume: 'asc' })
  const p = r.portofoliuManager
  if (!p) return null
  const m = r.manager
  const mAfisat = r.managerAfisat ?? m
  const randuri = sortare.ordoneaza(
    r.agenti.filter((a) => a.grup === 'manager' || a.grup === 'nationale'),
    (a, k) => (k === 'var' ? (a.vanzariComparatie ? a.vanzari / a.vanzariComparatie : null) : a[k]),
  )
  const auCumparat = r.clienti.filter(
    (c) => c.agentCheie === m && !c.faraVanzari && !c.national && c.vanzari !== 0,
  ).length
  const prenume = mAfisat?.split(' ')[0] ?? ''
  const managerRand = r.agenti.find((a) => a.grup === 'manager')
  return (
    <section
      className="grid gap-3 rounded-xl border border-b4l-border bg-b4l-bg-soft/60 p-3 sm:p-4"
      aria-labelledby="titlu-portofoliu"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="grid gap-0.5">
          <h3 id="titlu-portofoliu" className="text-sm font-semibold">
            {mAfisat ? `Portofoliul lui ${mAfisat}` : 'Conturi naționale'}
          </h3>
          {m && (
            <p className="max-w-[80ch] text-xs text-b4l-text-muted">
              {prenume} urmărește echipa, de aceea clienții lui stau aici și nu intră în totalurile
              echipei. {p.clientiPortofoliu} clienți alocați lui în CRM (fără conturile naționale),
              {` ${auCumparat}`} au cumpărat în perioadă.
            </p>
          )}
        </div>
        {m && (
          <button
            type="button"
            onClick={() => onPanou(managerRand?.cheie ?? m)}
            className="text-sm text-b4l-blue-dark hover:underline"
          >
            Costurile lui {prenume} →
          </button>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
        <table className="w-full min-w-[900px] text-sm">
          <CapAgenti r={r} sortare={sortare} primul="Grup" />
          <tbody>
            {randuri.map((a) => (
              <RandAgent key={a.cheie} a={a} r={r} max={1} bara={false} onDeschide={onClienti} />
            ))}
            {!randuri.length && (
              <tr>
                <td colSpan={10} className="px-2 py-4 text-center text-b4l-text-muted">
                  Nicio vânzare în perioada asta.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="text-[13px]">
            <tr className="border-t-2 border-b4l-border font-semibold">
              <td className="px-2 py-2" colSpan={5}>
                Total portofoliu{m ? `, după costurile lui ${prenume}` : ''}
                {p.total.costuri && p.total.costuri.total > 0 && (
                  <span className="font-normal text-b4l-text-muted">
                    {' '}
                    (− {lei(p.total.costuri.total)} lei costurile lui)
                  </span>
                )}
              </td>
              <td className="px-2 py-2 text-right">
                <Semnat n={p.total.profit} />
              </td>
              <td className="px-2 py-2 text-right">
                <Semnat n={p.total.profitDupaCosturi} />
              </td>
              <td className="px-2 py-2 text-right tabular-nums">{pct(p.total.marja)}</td>
              <td className="px-2 py-2 text-right tabular-nums">{p.total.clienti}</td>
              {r.comparatie && (
                <td className="px-2 py-2 text-right">
                  <Variatie
                    acum={p.total.vanzari}
                    trecut={p.total.comparatie?.vanzari ?? null}
                    eticheta={r.comparatie.eticheta}
                  />
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
      {p.laManagerDinEa.vanzari !== 0 && (
        <p className="text-xs text-b4l-text-muted">
          Include {lei(p.laManagerDinEa.vanzari)} lei de la {p.laManagerDinEa.clienti}{' '}
          {p.laManagerDinEa.clienti === 1 ? 'client alocat' : 'clienți alocați'} lui {prenume} în
          CRM, dar facturați în ExpertAccounts pe alt nume (de obicei al unui agent plecat).
        </p>
      )}
    </section>
  )
}

/**
 * „Total firmă" — rândul discret: echipa + portofoliul managerului + clienții fără agent. Clienții
 * fără agent nu stau în tabel (Mădălin, 04.11.2026), dar nici nu dispar: suma lor se spune aici.
 */
/** Ce intră în „Costuri flotă nealocate", pe un rând de explicație. */
function textFlota(f: FlotaNealocata): string {
  const parti = [
    f.laFirma ? `${lei(f.laFirma)} lei cu mașina la sediu` : null,
    f.faraAgent ? `${lei(f.faraAgent)} lei la oameni care nu sunt agenți` : null,
    f.faraPv ? `${lei(f.faraPv)} lei fără proces-verbal (după 30.11.2026)` : null,
  ].filter(Boolean)
  return `Carburantul pe care procesele-verbale nu-l dau niciunui agent: ${parti.join(', ')}${f.masini.length ? ` (${f.masini.join(', ')})` : ''}. Se scade din totalul firmei, nu din echipă.`
}

function TotalFirma({ r }: { r: Raport }) {
  const f = r.firma
  const fa = r.faraAgent
  const fl = r.costuriFlotaNealocate
  const poateAloca = useAuthStore(selectHasPermission('operational.alocare:read'))
  const parti = [
    'echipa',
    r.portofoliuManager
      ? r.manager
        ? `portofoliul lui ${r.managerAfisat}`
        : 'conturile naționale'
      : null,
    fa.clienti ? 'clienții fără agent' : null,
  ].filter(Boolean)
  return (
    <section
      className="grid gap-1 rounded-xl border border-dashed border-b4l-border px-3 py-2.5 text-[13px] text-b4l-text-muted"
      aria-label="Total firmă"
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-semibold text-b4l-text">Total firmă</span>
        <span>
          vânzări <span className="tabular-nums text-b4l-text">{lei(f.vanzari)}</span> lei
        </span>
        <span>
          profit din vânzări <Semnat n={f.profit} className="text-b4l-text" /> lei
        </span>
        <span>
          după costurile tuturor oamenilor de vânzări{fl && fl.total !== 0 ? ' și ale flotei' : ''}{' '}
          <Semnat n={f.profitDupaCosturi} className="text-b4l-text" /> lei
        </span>
        {fl && fl.total !== 0 && (
          <Explicatie text={textFlota(fl)}>
            <span>
              (din care − {lei(fl.total)} lei{' '}
              {ETICHETE_CIFRE.costuriFlotaNealocate.eticheta.toLowerCase()})
            </span>
          </Explicatie>
        )}
        {r.comparatie && f.comparatie && (
          <span>{textVariatie(f.vanzari, f.comparatie.vanzari, r.comparatie.eticheta)}</span>
        )}
      </div>
      <p className="text-xs">
        Adună {parti.join(' + ')}.
        {fa.clienti > 0 && (
          <>
            {' '}
            Include {lei(fa.vanzari)} lei de la {fa.clienti}{' '}
            {fa.clienti === 1 ? 'client fără agent alocat' : 'clienți fără agent alocat'}.{' '}
            {poateAloca && (
              <Link
                to="/operational/alocare-clienti"
                className="text-b4l-blue-dark hover:underline"
              >
                Alocă-i unui agent →
              </Link>
            )}
          </>
        )}
      </p>
    </section>
  )
}

function PanouAgent({
  r,
  cheie,
  onInchide,
  onClienti,
}: {
  r: Raport
  cheie: string
  onInchide: () => void
  onClienti: (k: string) => void
}) {
  const a = r.agenti.find((x) => x.cheie === cheie)
  // Managerul fara vanzari directe in perioada: doar costurile lui, ca sa le poata completa.
  if (!a && cheie === r.manager)
    return (
      <PanouLateral
        deschis
        onInchide={onInchide}
        titlu={r.managerAfisat ?? cheie}
        subtitlu={<Pastila ton="gri">fără vânzări directe în perioadă</Pastila>}
      >
        <CosturiAgent agent={cheie} an={Number(r.interval.pana.slice(0, 4))} />
      </PanouLateral>
    )
  if (!a) return null
  const ai = r.clienti
    .filter((c) => c.agentCheie === cheie && !c.faraVanzari)
    .sort((x, y) => y.profit - x.profit)
  const capete = ai.length > 6 ? [...ai.slice(0, 3), ...ai.slice(-3)] : ai
  const eManager = a.grup === 'manager'
  return (
    <PanouLateral
      deschis
      onInchide={onInchide}
      titlu={eManager ? (r.managerAfisat ?? a.nume) : numeRand(a, r)}
      subtitlu={
        <Pastila ton="gri">
          {eManager ? 'clienți direcți · ' : ''}
          {a.clienti} clienți cu vânzări
        </Pastila>
      }
    >
      <Mini m={a} />
      <SectiunePanou titlu="Cum se ajunge la profit">
        <Cascada
          m={a}
          costuri={a.costuri}
          etichetaCosturi="Costurile agentului"
          etichetaFinal={eManager ? `Rămas după costurile lui` : 'Profit rămas după agent'}
        />
        {eManager && r.portofoliuManager && (
          <p className="mt-2 text-xs text-b4l-text-muted">
            Doar clienții lui direcți. Conturile naționale au rândul lor în portofoliu; totalul
            portofoliului după costurile lui:{' '}
            <Semnat n={r.portofoliuManager.total.profitDupaCosturi} /> lei.
          </p>
        )}
        {a.costuri && a.costuri.luniLipsa.length > 0 && (
          <p className="mt-2 text-xs text-b4l-amber-dark">
            <Steluta /> Salariu necompletat: {textLipsa(a.costuri.luniLipsa)} — cifra pare mai mare.
          </p>
        )}
        {a.costuri && a.costuri.masini.length > 0 && (
          <p className="mt-1 text-xs text-b4l-text-muted">
            Carburant din Fill&amp;Go: {a.costuri.masini.join(', ')} (fără TVA, plus TVA-ul
            nedeductibil), după procesele-verbale de predare — cine avea mașina la alimentare.
            {(a.costuri.carburantFaraPv ?? 0) > 0 && (
              <span className="text-b4l-amber-dark">
                {' '}
                Din el, {lei(a.costuri.carburantFaraPv!)} lei n-au PV pe perioadă și s-au pus după
                șoferul trecut pe mașină; se corectează singuri când se introduc PV-urile.
              </span>
            )}
          </p>
        )}
      </SectiunePanou>
      {a.costuri && <CosturiAgent agent={a.cheie} an={Number(r.interval.pana.slice(0, 4))} />}
      {capete.length > 0 && (
        <SectiunePanou titlu={ai.length > 6 ? 'Cei mai profitabili și cei mai slabi' : 'Clienții'}>
          <table className="w-full text-[13px]">
            <tbody>
              {capete.map((c) => (
                <tr key={c.cheie} className="border-t border-b4l-border">
                  <td className="py-1 pr-2">{c.nume}</td>
                  <td className="py-1 text-right">
                    <Semnat n={c.profit} />
                  </td>
                  <td className="py-1 pl-2 text-right tabular-nums">{pct(c.marja)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectiunePanou>
      )}
      <button
        type="button"
        onClick={() => onClienti(a.cheie)}
        className="justify-self-start rounded-md bg-b4l-blue px-3 py-1.5 text-sm font-medium text-b4l-pe-accent"
      >
        Vezi clienții{' '}
        {a.grup === 'nationale'
          ? ''
          : `lui ${(eManager ? (r.managerAfisat ?? a.nume) : a.nume).split(' ')[0]}`}{' '}
        →
      </button>
    </PanouLateral>
  )
}

/** Bara din coloana Vânzări: loc fix de 60px, umplerea plafonată la 100%. */
function BaraVanzari({ v, max }: { v: number | null; max: number }) {
  const fractie = v == null ? 0 : Math.min(1, Math.max(0, v) / max)
  return (
    <span className="inline-block w-[60px] shrink-0">
      {v != null && (
        <span className="block h-2 rounded bg-b4l-blue/40" style={{ width: `${fractie * 100}%` }} />
      )}
    </span>
  )
}

function TabelClienti({
  r,
  clienti,
  chip,
  setChip,
  onDeschide,
}: {
  r: Raport
  clienti: Client[]
  chip: ChipClienti
  setChip: (c: ChipClienti) => void
  onDeschide: (k: string) => void
}) {
  const sortare = useSortare<ColClient>('vanzari', { nume: 'asc' })
  const filtre: Record<ChipClienti, (c: Client) => boolean> = {
    toti: (c) => !c.faraVanzari,
    sub: (c) => c.profit < 0,
    fara: (c) => !!c.faraVanzari,
    slab: (c) => c.marja != null && c.marja < r.pragMarja,
    serv: (c) => c.serviciiPct != null && c.serviciiPct > r.pragServicii,
    lipsa: (c) => c.vanzariFaraCost !== 0,
    clasif: (c) => c.deClasificatNr > 0,
  }
  const vizibili = sortare.ordoneaza(clienti.filter(filtre[chip]), (c, k) =>
    k === 'var' ? (c.vanzariComparatie ? c.vanzari / c.vanzariComparatie : null) : c[k],
  )
  const max = Math.max(1, ...clienti.map((c) => c.vanzari))
  return (
    <div className="grid gap-3">
      <ChipuriFiltru<ChipClienti>
        ales={chip}
        onAlege={setChip}
        chipuri={[
          { cheie: 'toti', eticheta: 'Cu vânzări', numar: clienti.filter(filtre.toti).length },
          { cheie: 'sub', eticheta: 'Sub zero', numar: clienti.filter(filtre.sub).length },
          {
            cheie: 'slab',
            eticheta: `Marjă sub ${r.pragMarja}%`,
            numar: clienti.filter(filtre.slab).length,
          },
          {
            cheie: 'serv',
            eticheta: `Servicii peste ${r.pragServicii}%`,
            numar: clienti.filter(filtre.serv).length,
          },
          { cheie: 'lipsa', eticheta: 'Cost lipsă', numar: clienti.filter(filtre.lipsa).length },
          {
            cheie: 'clasif',
            eticheta: 'De clasificat',
            numar: clienti.filter(filtre.clasif).length,
          },
          ...(clienti.some(filtre.fara)
            ? [
                {
                  cheie: 'fara' as const,
                  eticheta: 'Fără vânzări în perioadă',
                  numar: clienti.filter(filtre.fara).length,
                },
              ]
            : []),
        ]}
      />
      <div className="overflow-x-auto rounded-xl border border-b4l-border bg-b4l-surface">
        <table className="w-full min-w-[880px] text-sm">
          <thead className="text-xs text-b4l-text-muted">
            <tr>
              <CapSortabil sortare={sortare} coloana="nume">
                Client
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="vanzari" dreapta explicatie={coloane.vanzari}>
                Vânzări
              </CapSortabil>
              <CapSortabil
                sortare={sortare}
                coloana="cantitate"
                dreapta
                explicatie={coloane.cantitate}
              >
                Buc.
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="cost" dreapta explicatie={coloane.cost}>
                Costul mărfii
              </CapSortabil>
              <CapSortabil
                sortare={sortare}
                coloana="servicii"
                dreapta
                explicatie={coloane.servicii}
              >
                Servicii clienți
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="profit" dreapta explicatie={coloane.profit}>
                Profit
              </CapSortabil>
              <CapSortabil sortare={sortare} coloana="marja" dreapta explicatie={coloane.marja}>
                Marjă
              </CapSortabil>
              {r.comparatie && (
                <CapSortabil
                  sortare={sortare}
                  coloana="var"
                  dreapta
                  explicatie={`Vânzările față de ${r.comparatie.eticheta}.`}
                >
                  Față de {r.comparatie.eticheta}
                </CapSortabil>
              )}
            </tr>
          </thead>
          <tbody>
            {vizibili.map((c) => (
              <tr
                key={c.cheie}
                tabIndex={c.faraVanzari ? undefined : 0}
                onClick={() => !c.faraVanzari && onDeschide(c.cheie)}
                onKeyDown={(e) => e.key === 'Enter' && !c.faraVanzari && onDeschide(c.cheie)}
                className={`border-t border-b4l-border ${c.faraVanzari ? 'text-b4l-text-muted' : 'cursor-pointer hover:bg-b4l-bg-soft'}`}
              >
                <td className="px-3 py-2 font-medium">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {c.nume}
                    {c.id == null && (
                      <Explicatie text="Partenerul din factură nu e găsit în registrul de clienți din CRM.">
                        <Pastila ton="gri">neînregistrat</Pastila>
                      </Explicatie>
                    )}
                    {c.faraVanzari && <Pastila ton="gri">fără vânzări în perioadă</Pastila>}
                    <Semne m={c} prag={r.pragServicii} />
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  <div className="flex items-center justify-end gap-2">
                    {lei(c.vanzari)}
                    <BaraVanzari v={c.vanzari} max={max} />
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{lei(c.cantitate)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{lei(c.cost)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {c.servicii ? lei(c.servicii) : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <Semnat n={c.profit} />
                </td>
                <td
                  className={`px-3 py-2 text-right tabular-nums ${(c.marja ?? 0) < 0 ? 'text-b4l-red-dark' : ''}`}
                >
                  {pct(c.marja)}
                </td>
                {r.comparatie && (
                  <td className="px-3 py-2 text-right">
                    <Variatie
                      acum={c.vanzari}
                      trecut={c.vanzariComparatie}
                      eticheta={r.comparatie.eticheta}
                    />
                  </td>
                )}
              </tr>
            ))}
            {!vizibili.length && (
              <tr>
                <td
                  colSpan={r.comparatie ? 8 : 7}
                  className="px-3 py-6 text-center text-b4l-text-muted"
                >
                  Niciun client aici.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Mini({ m }: { m: Metrici }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {[
        [
          'Vânzări',
          <span key="v" className="tabular-nums">
            {lei(m.vanzari)}
          </span>,
        ],
        ['Profit', <Semnat key="p" n={m.profit} />],
        [
          'Marjă',
          <span key="m" className={(m.marja ?? 0) < 0 ? 'text-b4l-red-dark' : ''}>
            {pct(m.marja)}
          </span>,
        ],
      ].map(([e, v]) => (
        <div key={String(e)} className="rounded-lg bg-b4l-bg-soft px-3 py-2">
          <div className="text-[11px] uppercase tracking-wider text-b4l-text-dim">{e}</div>
          <div className="text-[15px] font-semibold">{v}</div>
        </div>
      ))}
    </div>
  )
}

const sumaSauNull = (v: string): number | null => {
  const t = v.replace(/\s/g, '').replace(',', '.')
  if (t === '') return null
  const x = Number(t)
  return Number.isFinite(x) && x >= 0 ? Math.round(x * 100) / 100 : null
}
/*
  Campurile din tabel. In baza stau costul (net + taxe) si netul; taxele se calculeaza:
  taxe = cost − net. Asa totalurile vechi raman neatinse, iar Victor vede netul separat de taxe.
*/
type CampCost = 'salariuNet' | 'taxeSalariu' | 'bonusNet' | 'taxeBonus' | 'auto' | 'carburant'
const CAMPURI_COST: [CampCost, string][] = [
  ['salariuNet', 'Salariu net'],
  ['taxeSalariu', 'Taxe salariu'],
  ['bonusNet', 'Bonus net'],
  ['taxeBonus', 'Taxe bonus'],
  ['auto', 'Mașină'],
  ['carburant', 'Carburant'],
]
const r2c = (x: number) => Math.round(x * 100) / 100
/** Valoarea afisata a unui camp, din ce e salvat. */
function valoareCamp(l: LunaCost, k: CampCost): number | null {
  if (k === 'taxeSalariu')
    return l.salariuTaxe == null ? null : r2c(l.salariuTaxe - (l.salariuNet ?? 0))
  if (k === 'taxeBonus') return l.bonus == null ? null : r2c(l.bonus - (l.bonusNet ?? 0))
  return l[k]
}

/** Costurile agentului pe lunile anului: completate din statul de plată, carburantul din Fill&Go. */
function CosturiAgent({ agent, an }: { agent: string; an: number }) {
  const poate = useAuthStore(selectHasPermission('vanzari.profitabilitate:write'))
  const qc = useQueryClient()
  const q = useQuery<{ items: LunaCost[] }>({
    queryKey: ['profitabilitate-costuri', agent, an],
    queryFn: async () =>
      (
        await api.get<{ items: LunaCost[] }>('/profitabilitate/costuri-agent', {
          params: { agent, an },
        })
      ).data,
  })
  const [ciorna, setCiorna] = useState<Record<string, Partial<Record<CampCost, string>>>>({})
  const salveaza = useMutation({
    mutationFn: async (l: LunaCost) => {
      const c = ciorna[l.luna] ?? {}
      const val = (k: CampCost) => {
        const scris = c[k]
        return scris !== undefined ? sumaSauNull(scris) : valoareCamp(l, k)
      }
      // Costul = net + taxe; gol daca ambele sunt goale.
      const cost = (net: number | null, taxe: number | null) =>
        net == null && taxe == null ? null : r2c((net ?? 0) + (taxe ?? 0))
      const salariuNet = val('salariuNet')
      const bonusNet = val('bonusNet')
      const salariuTaxe = cost(salariuNet, val('taxeSalariu'))
      const bonus = cost(bonusNet, val('taxeBonus'))
      return api.put('/profitabilitate/costuri-agent', {
        agent,
        luna: l.luna,
        salariuTaxe,
        bonus,
        salariuNet: salariuTaxe == null ? null : salariuNet,
        bonusNet: bonus == null ? null : bonusNet,
        auto: val('auto'),
        carburant: val('carburant'),
      })
    },
    onSuccess: (_d, l) => {
      toast.success(`Costurile pe ${etLuna(l.luna)} s-au salvat.`)
      setCiorna((x) => Object.fromEntries(Object.entries(x).filter(([k]) => k !== l.luna)))
      void qc.invalidateQueries({ queryKey: ['profitabilitate'] })
      void qc.invalidateQueries({ queryKey: ['profitabilitate-costuri', agent] })
    },
    onError: () => toast.error('Costurile nu s-au salvat. Verifică sumele.'),
  })
  const luni = q.data?.items ?? []
  return (
    <SectiunePanou titlu={`Costurile agentului pe ${an} (estimat)`}>
      {q.isLoading ? (
        <Spinner />
      ) : (
        <div
          // w-0 + min-w-full: tabelul (6 coloane) se deruleaza in el, fara sa latesca panoul;
          // altfel cifrele din dreapta cascadei ieseau din ecran (04.11.2026).
          className="w-0 min-w-full overflow-x-auto"
        >
          <table className="w-full text-xs">
            <thead className="text-b4l-text-muted">
              <tr>
                <th className="py-1 text-left font-medium">Luna</th>
                {CAMPURI_COST.map(([k, e]) => (
                  <th key={k} className="py-1 text-right font-medium">
                    {e}
                  </th>
                ))}
                {poate && <th />}
              </tr>
            </thead>
            <tbody>
              {luni.map((l) => {
                const c = ciorna[l.luna]
                return (
                  <tr key={l.luna} className="border-t border-b4l-border">
                    <td className="whitespace-nowrap py-1 pr-1">{etLuna(l.luna)}</td>
                    {CAMPURI_COST.map(([k, e]) => {
                      const sugestie =
                        k === 'carburant' && l.carburantFillGo ? lei(l.carburantFillGo) : ''
                      const v = valoareCamp(l, k)
                      const scris = c?.[k]
                      return (
                        <td key={k} className="py-0.5 pl-1 text-right">
                          {poate ? (
                            <input
                              inputMode="decimal"
                              aria-label={`${e}, ${etLuna(l.luna)}`}
                              value={scris !== undefined ? scris : v == null ? '' : String(v)}
                              placeholder={sugestie}
                              onChange={(ev) =>
                                setCiorna((x) => ({
                                  ...x,
                                  [l.luna]: { ...x[l.luna], [k]: ev.target.value },
                                }))
                              }
                              className="w-[64px] rounded border border-b4l-border bg-b4l-surface px-1 py-0.5 text-right tabular-nums placeholder:text-b4l-text-dim"
                            />
                          ) : (
                            <span className="tabular-nums">
                              {v != null ? lei(v) : sugestie || '—'}
                            </span>
                          )}
                        </td>
                      )
                    })}
                    {poate && (
                      <td className="py-0.5 pl-1">
                        {c && (
                          <button
                            type="button"
                            disabled={salveaza.isPending}
                            onClick={() => salveaza.mutate(l)}
                            className="rounded bg-b4l-blue px-1.5 py-0.5 text-[11px] font-medium text-b4l-pe-accent disabled:opacity-50"
                          >
                            Salvează
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-b4l-text-muted">
            Costul firmei, fără TVA. Net = cât primește agentul în mână (din statul de plată); taxe
            = CAS, CASS, impozit și CAM (2,25%, plătit de firmă). Taxele bonusului sunt la cotele
            legale; restul taxelor din stat sunt ale salariului. Carburantul gol se ia din
            Fill&amp;Go (cifra gri); o sumă scrisă îl înlocuiește.
          </p>
        </div>
      )}
    </SectiunePanou>
  )
}

type FilaClient = 'produse' | 'facturi' | 'primite'

function PanouClient({
  r,
  cheie,
  sel,
  onInchide,
}: {
  r: Raport
  cheie: string
  sel: Selectie
  onInchide: () => void
}) {
  const c = r.clienti.find((x) => x.cheie === cheie)
  const [fila, setFila] = useState<FilaClient>('produse')
  const poateClasifica = useAuthStore(selectHasPermission('vanzari.profitabilitate:write'))
  const qc = useQueryClient()
  const d = useQuery<Detaliu>({
    queryKey: ['profitabilitate-client', sel, cheie],
    queryFn: async () =>
      (
        await api.get<Detaliu>('/profitabilitate/client', {
          params: { ...paramsSelectie(sel), cheie },
          timeout: TIMP_CALCUL,
        })
      ).data,
  })
  const clasifica = useMutation({
    mutationFn: async ({ id, tip }: { id: number; tip: TipFactura | null }) =>
      api.put(`/profitabilitate/facturi-primite/${id}/clasificare`, { tip }),
    onSuccess: () => {
      toast.success('Clasificarea s-a salvat. Profitul s-a recalculat.')
      void qc.invalidateQueries({ queryKey: ['profitabilitate'] })
      void qc.invalidateQueries({ queryKey: ['profitabilitate-client'] })
    },
    onError: () => toast.error('Clasificarea nu s-a salvat. Încearcă din nou.'),
  })
  const salveazaPerioada = useMutation({
    mutationFn: async ({
      id,
      perioada,
    }: {
      id: number
      perioada: { de: string; pana: string } | null
    }) => api.put(`/profitabilitate/facturi-primite/${id}/perioada`, { perioada }),
    onSuccess: () => {
      toast.success('Perioada s-a salvat. Profitul s-a recalculat.')
      void qc.invalidateQueries({ queryKey: ['profitabilitate'] })
      void qc.invalidateQueries({ queryKey: ['profitabilitate-client'] })
    },
    onError: () => toast.error('Perioada nu s-a salvat. Încearcă din nou.'),
  })
  if (!c) return null
  const agent = r.agenti.find((a) => a.cheie === c.agentCheie)

  return (
    <PanouLateral
      deschis
      onInchide={onInchide}
      titlu={c.nume}
      subtitlu={
        <>
          <Pastila ton="gri">{agent ? numeRand(agent, r) : c.agentCheie}</Pastila>
          <Pastila ton="gri">{r.eticheta}</Pastila>
        </>
      }
    >
      <Mini m={c} />
      <SectiunePanou titlu="Cum s-a calculat">
        <Cascada m={c} />
      </SectiunePanou>
      {c.profit < 0 && c.servicii > 0 && (
        <p className="rounded-lg bg-b4l-bg-soft px-3 py-2 text-[13px]">
          Serviciile plătite clientului ({lei(c.servicii)} lei, adică {pct(c.serviciiPct)} din
          vânzări) au trecut de marja pe produse ({lei(c.vanzari - c.cost)} lei).
        </p>
      )}
      {c.vanzariFaraCost !== 0 && (
        <p className="rounded-lg bg-b4l-amber-light px-3 py-2 text-[13px] text-b4l-amber-dark">
          Pentru {lei(c.vanzariFaraCost)} lei din vânzări n-am găsit prețul de intrare: profitul
          poate fi mai mic cu cel mult atât.
        </p>
      )}

      <ChipuriFiltru<FilaClient>
        ales={fila}
        onAlege={setFila}
        eticheta="Detalii"
        chipuri={[
          { cheie: 'produse', eticheta: 'Produse', numar: d.data?.produse.length },
          { cheie: 'facturi', eticheta: 'Facturi vânzare', numar: d.data?.facturi.length },
          { cheie: 'primite', eticheta: 'Facturi primite', numar: d.data?.facturiPrimite.length },
        ]}
      />
      {d.isLoading && <Spinner />}
      {d.isError && <p className="text-sm text-b4l-red-dark">Detaliile nu s-au putut citi.</p>}
      {d.data && fila === 'produse' && (
        <table className="w-full text-[12.5px]">
          <thead className="text-b4l-text-muted">
            <tr>
              <th className="py-1 text-left font-medium">Produs</th>
              <th className="py-1 text-right font-medium">Buc.</th>
              <th className="py-1 text-right font-medium">Vânzări</th>
              <th className="py-1 text-right font-medium">Cost</th>
              <th className="py-1 text-right font-medium">Marjă</th>
            </tr>
          </thead>
          <tbody>
            {d.data.produse.map((p) => (
              <tr key={p.sku} className="border-t border-b4l-border">
                <td className="py-1 pr-2">
                  <div>{p.denumire}</div>
                  <div className="text-[11px] text-b4l-text-dim">{p.sku}</div>
                </td>
                <td className="py-1 text-right tabular-nums">{lei(p.cantitate)}</td>
                <td className="py-1 text-right tabular-nums">{lei(p.vanzari)}</td>
                <td className="py-1 text-right tabular-nums">
                  {p.cost == null ? (
                    <Pastila ton="galben">fără cost</Pastila>
                  ) : (
                    <Explicatie
                      text={`Sursa: ${p.surse.map((s) => ETICHETA_SURSA[s]).join('; ')}.`}
                    >
                      {lei(p.cost)}
                    </Explicatie>
                  )}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {p.cost == null || p.vanzari === 0
                    ? '—'
                    : pct(Math.round(((p.vanzari - p.cost) / p.vanzari) * 1000) / 10)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {d.data && fila === 'facturi' && (
        <table className="w-full text-[12.5px]">
          <tbody>
            {d.data.facturi.map((f) => (
              <tr key={f.numar} className="border-t border-b4l-border">
                <td className="py-1">
                  {f.numar} {f.storno && <Pastila ton="gri">storno</Pastila>}
                </td>
                <td className="py-1 text-b4l-text-muted">{dmy(f.data)}</td>
                <td className="py-1 text-right">
                  <Semnat n={f.valoare} />
                </td>
              </tr>
            ))}
            {!d.data.facturi.length && (
              <tr>
                <td className="py-2 text-b4l-text-muted">Nicio factură de vânzare în perioadă.</td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      {d.data && fila === 'primite' && (
        <div className="grid gap-2">
          {d.data.facturiPrimite.map((f) => (
            <div
              key={f.id}
              className="grid gap-1 rounded-lg border border-b4l-border px-3 py-2 text-[12.5px]"
            >
              <div className="flex flex-wrap justify-between gap-2">
                <span className="font-medium">{f.numar ?? 'fără număr'}</span>
                <span className="tabular-nums">
                  {f.valoare === f.valoareTotala ? (
                    <>{lei(f.valoare)} lei fără TVA</>
                  ) : (
                    <Explicatie
                      text={`Factura e de ${lei(f.valoareTotala)} lei fără TVA, împărțită egal pe ${f.luni.length} luni. În perioada aleasă intră doar partea lunilor ei.`}
                    >
                      {lei(f.valoare)} lei în perioadă, din {lei(f.valoareTotala)}
                    </Explicatie>
                  )}
                </span>
              </div>
              <div className="text-b4l-text-muted">
                emisă {dmy(f.data)} · {f.emitent ?? '—'}
              </div>
              <PerioadaFacturii
                key={`${f.id}-${f.luni.join(',')}`}
                id={f.id}
                luni={f.luni}
                sursa={f.sursaPerioada}
                poateSchimba={poateClasifica}
                ocupat={salveazaPerioada.isPending}
                onSalveaza={(p) => salveazaPerioada.mutate({ id: f.id, perioada: p })}
              />
              {poateClasifica ? (
                <label className="flex items-center gap-2">
                  <span className="text-b4l-text-muted">Ce este:</span>
                  <select
                    id={`clasificare-${f.id}`}
                    value={f.tip ?? ''}
                    disabled={clasifica.isPending}
                    onChange={(e) =>
                      clasifica.mutate({
                        id: f.id,
                        tip: (e.target.value || null) as TipFactura | null,
                      })
                    }
                    className="rounded-md border border-b4l-border bg-b4l-surface px-2 py-1"
                  >
                    <option value="">De clasificat (nu scade profitul)</option>
                    {(Object.keys(ETICHETA_TIP) as TipFactura[]).map((t) => (
                      <option key={t} value={t}>
                        {ETICHETA_TIP[t]}
                      </option>
                    ))}
                  </select>
                  {f.sursaTip === 'automat' && <PastilaAutomat />}
                </label>
              ) : (
                <span className="flex items-center gap-2">
                  {f.tip ? ETICHETA_TIP[f.tip] : 'De clasificat'}
                  {f.sursaTip === 'automat' && <PastilaAutomat />}
                </span>
              )}
            </div>
          ))}
          {!d.data.facturiPrimite.length && (
            <p className="text-b4l-text-muted">
              Nicio factură primită de la acest client în perioadă.
            </p>
          )}
          <p className="text-[11.5px] text-b4l-text-dim">
            Doar marketingul, bonusul de volum și raftul scad profitul. „Altele” (de exemplu marfă
            cumpărată de la un distribuitor) nu. Costul intră în luna la care se referă factura, nu
            în luna emiterii; o factură pe mai multe luni se împarte egal pe ele.
          </p>
        </div>
      )}
    </PanouLateral>
  )
}
